/**
 * Pure state machine behind `useItemSelection`.
 *
 * Two facts are tracked separately on purpose:
 *  - `mode`        - the user has explicitly entered multi-select mode, so every row
 *                    shows its selection circle and the bulk action bar stays up.
 *  - `selectedIds` - which items are currently selected.
 *
 * Invariant (D-6 of CHECKLIST_EDIT_MODE_UI_PLAN): a non-empty `selectedIds` implies
 * `mode === true`. Deselecting back to empty keeps the mode on - the user is still
 * selecting - so only `exitMode` ever turns it off. That is what lets ctrl-click,
 * shift-click, long-press and "Select all N matches" act as implicit entry points
 * without any of them special-casing the mode flag.
 */
export type ItemSelectionState = {
  mode: boolean;
  selectedIds: ReadonlySet<string>;
  anchorId: string | null;
};

export type ItemSelectionAction =
  | { type: "enterMode" }
  | { type: "exitMode" }
  | { type: "clearSelection" }
  | { type: "toggleOne"; id: string }
  | { type: "selectMany"; ids: readonly string[] }
  | { type: "deselectMany"; ids: readonly string[] }
  | { type: "setAnchor"; id: string | null }
  | { type: "longPress"; id: string };

export const initialItemSelectionState: ItemSelectionState = {
  mode: false,
  selectedIds: new Set<string>(),
  anchorId: null,
};

export function itemSelectionReducer(
  state: ItemSelectionState,
  action: ItemSelectionAction
): ItemSelectionState {
  switch (action.type) {
    case "enterMode":
      return state.mode ? state : { ...state, mode: true };

    case "exitMode":
      return state.mode || state.selectedIds.size > 0 || state.anchorId !== null
        ? initialItemSelectionState
        : state;

    case "clearSelection":
      return state.selectedIds.size === 0 && state.anchorId === null
        ? state
        : { ...state, selectedIds: new Set<string>(), anchorId: null };

    case "toggleOne": {
      const next = new Set(state.selectedIds);
      if (next.has(action.id)) next.delete(action.id);
      else next.add(action.id);
      return { ...state, mode: state.mode || next.size > 0, selectedIds: next };
    }

    case "selectMany": {
      if (action.ids.length === 0) return state;
      const next = new Set(state.selectedIds);
      for (const id of action.ids) next.add(id);
      return { ...state, mode: true, selectedIds: next };
    }

    case "deselectMany": {
      if (action.ids.length === 0) return state;
      const next = new Set(state.selectedIds);
      for (const id of action.ids) next.delete(id);
      return { ...state, selectedIds: next };
    }

    case "setAnchor":
      return state.anchorId === action.id ? state : { ...state, anchorId: action.id };

    case "longPress": {
      const next = new Set(state.selectedIds);
      next.add(action.id);
      return { mode: true, selectedIds: next, anchorId: action.id };
    }
  }
}
